import { SideDrawer } from "@/components/common/SideDrawer";
import { TaskComposerInline } from "@/resources/tasks/components/task-list/TaskComposerInline";
import { TaskDetailsDialog } from "@/resources/tasks/components/task-dialogs/TaskDetailsDialog";
import { CompactTaskList } from "@/resources/tasks/components/task-list/CompactTaskList";
import { Task, ObjectType, TaskRecordReference } from "@/types";
import { useState, useMemo } from "react";
import { useGetList, useRecordContext } from "react-admin";
import { Box, Typography, Collapse, ButtonBase, Chip } from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  People as ClaimantIcon,
  Policy as PolicyIcon,
  Business as ClientIcon,
  LocalHospital as ClaimIcon,
  Payments as ReimbursementIcon,
} from "@mui/icons-material";

// ─── Record type grouping config ──────────────────────────────────────────

interface TaskGroup {
  key: string;
  label: string;
  icon: React.ReactNode;
  types: string[];
}

const TASK_GROUPS: TaskGroup[] = [
  { key: "clients", label: "Client", icon: <ClientIcon sx={{ fontSize: 16 }} />, types: ["clients"] },
  { key: "claimants", label: "Claimants", icon: <ClaimantIcon sx={{ fontSize: 16 }} />, types: ["claimants"] },
  { key: "policies", label: "Policies", icon: <PolicyIcon sx={{ fontSize: 16 }} />, types: ["policies"] },
  {
    key: "claims",
    label: "Claims",
    icon: <ClaimIcon sx={{ fontSize: 16 }} />,
    types: ["stoploss_claims", "claim_items"],
  },
  {
    key: "reimbursements",
    label: "Reimbursements",
    icon: <ReimbursementIcon sx={{ fontSize: 16 }} />,
    types: ["reimbursements"],
  },
];

// ─── Grouped task section ─────────────────────────────────────────────────

const TaskGroupSection = ({
  group,
  tasks,
  onTaskClick,
  showCheckbox = true,
  defaultExpanded = true,
}: {
  group: TaskGroup;
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  showCheckbox?: boolean;
  defaultExpanded?: boolean;
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (tasks.length === 0) return null;

  return (
    <Box sx={{ mb: 0.5 }}>
      <ButtonBase
        onClick={() => setExpanded(!expanded)}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          bgcolor: "action.hover",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.selected" },
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            color: "text.disabled",
            transition: "transform 0.2s",
            transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
          }}
        />
        <Box sx={{ color: "text.secondary", display: "flex" }}>{group.icon}</Box>
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase" }}>
          {group.label}
        </Typography>
        <Chip label={tasks.length} size="small" sx={{ height: 18, fontSize: "0.65rem", ml: "auto" }} />
      </ButtonBase>
      <Collapse in={expanded} timeout="auto">
        <Box sx={{ opacity: showCheckbox ? 1 : 0.6 }}>
          <CompactTaskList
            tasks={tasks}
            isLoading={false}
            onTaskClick={onTaskClick}
            showAssignedUser
            showRelatedTo
            showCheckbox={showCheckbox}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────

interface ClientSideDrawerProps {
  drawerExpanded: boolean;
  toggleDrawer: () => void;
}

export const ClientSideDrawer: React.FC<ClientSideDrawerProps> = ({ drawerExpanded, toggleDrawer }) => {
  const client = useRecordContext();
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Task | undefined>();
  const [showCompleted, setShowCompleted] = useState(false);

  const clientId = client?.id ?? null;

  // Fetch ALL ecosystem tasks via client_id_ref
  const {
    data: allTasks = [],
    isLoading,
    refetch,
  } = useGetList<Task>(
    `clients/${clientId}/tasks`,
    {
      filter: { deleted: false },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "due_date", order: "ASC" },
    },
    { enabled: !!clientId }
  );

  const openTasks = useMemo(() => allTasks.filter((t) => !t.completed), [allTasks]);
  const completedTasks = useMemo(() => allTasks.filter((t) => t.completed), [allTasks]);

  // Split client-level tasks from child record tasks (no header needed for client tasks)
  const clientOpenTasks = useMemo(() => openTasks.filter((t) => t.record?.object_type === "clients"), [openTasks]);
  const childOpenTasks = useMemo(() => openTasks.filter((t) => t.record?.object_type !== "clients"), [openTasks]);
  const clientCompletedTasks = useMemo(
    () => completedTasks.filter((t) => t.record?.object_type === "clients"),
    [completedTasks]
  );
  const childCompletedTasks = useMemo(
    () => completedTasks.filter((t) => t.record?.object_type !== "clients"),
    [completedTasks]
  );

  // Group child tasks by record type (exclude clients group)
  const CHILD_GROUPS = TASK_GROUPS.filter((g) => g.key !== "clients");

  const groupedChildOpenTasks = useMemo(() => {
    return CHILD_GROUPS.map((group) => ({
      group,
      tasks: childOpenTasks.filter((t) => group.types.includes(t.record?.object_type || "")),
    })).filter((g) => g.tasks.length > 0);
  }, [childOpenTasks]);

  const groupedChildCompletedTasks = useMemo(() => {
    return CHILD_GROUPS.map((group) => ({
      group,
      tasks: childCompletedTasks.filter((t) => group.types.includes(t.record?.object_type || "")),
    })).filter((g) => g.tasks.length > 0);
  }, [childCompletedTasks]);

  if (!client) return null;

  const recordRef = {
    object_type: ObjectType.CLIENTS as const,
    object_id: clientId,
    label: client.client_legal_name || client.aegle_id,
  } as TaskRecordReference;

  const handleTaskClick = (task: Task) => {
    setSelected(task);
    setOpen(true);
  };

  return (
    <>
      <SideDrawer title="Tasks" open={drawerExpanded} onClose={toggleDrawer} hideHandle width={500}>
        {/* Inline add, bound to this client — or to one of its claimants for a claimant template */}
        {/* Keyed by client so a claimant picked on one client never carries over to the next. */}
        <TaskComposerInline
          key={String(clientId)}
          record={recordRef}
          claimantClientId={String(clientId)}
          onCreated={() => {
            // The list is fetched here, not by the remounted children, so refetch it.
            void refetch();
            setRefreshKey((k) => k + 1);
          }}
          open={drawerExpanded}
        />

        {/* Open ecosystem tasks */}
        {isLoading ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="caption" color="text.disabled">
              Loading tasks…
            </Typography>
          </Box>
        ) : openTasks.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.disabled">
              No open tasks
            </Typography>
          </Box>
        ) : (
          <Box key={`open-${refreshKey}`} sx={{ mt: 0.5 }}>
            {/* Client-level tasks — no group header */}
            {clientOpenTasks.length > 0 && (
              <CompactTaskList
                tasks={clientOpenTasks}
                isLoading={false}
                onTaskClick={handleTaskClick}
                showAssignedUser
              />
            )}
            {/* Child record tasks — grouped with headers */}
            {groupedChildOpenTasks.map(({ group, tasks }) => (
              <TaskGroupSection key={group.key} group={group} tasks={tasks} onTaskClick={handleTaskClick} />
            ))}
          </Box>
        )}

        {/* Completed tasks — expandable, grouped */}
        {completedTasks.length > 0 && (
          <Box sx={{ mt: 1, borderTop: 1, borderColor: "divider" }}>
            <ButtonBase
              onClick={() => setShowCompleted(!showCompleted)}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Completed Tasks ({completedTasks.length})
              </Typography>
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: "text.disabled",
                  transition: "transform 0.2s",
                  transform: showCompleted ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </ButtonBase>
            <Collapse in={showCompleted} timeout="auto" unmountOnExit>
              <Box key={`completed-${refreshKey}`}>
                {/* Client-level completed — no group header, disabled look */}
                {clientCompletedTasks.length > 0 && (
                  <Box sx={{ opacity: 0.6 }}>
                    <CompactTaskList
                      tasks={clientCompletedTasks}
                      isLoading={false}
                      onTaskClick={handleTaskClick}
                      showAssignedUser
                      showCheckbox={false}
                    />
                  </Box>
                )}
                {/* Child record completed — grouped */}
                {groupedChildCompletedTasks.map(({ group, tasks }) => (
                  <TaskGroupSection
                    key={group.key}
                    group={group}
                    tasks={tasks}
                    onTaskClick={handleTaskClick}
                    showCheckbox={false}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </SideDrawer>

      <TaskDetailsDialog open={open} task={selected} onClose={() => setOpen(false)} />
    </>
  );
};
